import React, { useState, useEffect } from 'react';
import axios from '../utils/api';
import './GstRateManager.css';

const GstRateManager = () => {
  const [gstRates, setGstRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    onConfirm: null,
  });
  const [showForm, setShowForm] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [filterRateType, setFilterRateType] = useState('');

  const [formData, setFormData] = useState({
    hsn_code: '',
    rate_type: '',
    min_value: '',
    max_value: '',
    cgst_rate: '',
    sgst_rate: '',
    igst_rate: ''
  });

  useEffect(() => {
    fetchGstRates();
  }, []);

  const fetchGstRates = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/gst-rates');
      setGstRates(response.data);
    } catch (err) {
      setError('Failed to fetch GST rates');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let updatedData = { ...formData, [name]: value };

    // Auto-calculate SGST and IGST if CGST is changed
    if (name === 'cgst_rate') {
      const cgst = parseFloat(value) || 0;
      updatedData.sgst_rate = cgst.toString();
      updatedData.igst_rate = (cgst * 2).toString();
    }

    setFormData(updatedData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        min_value: parseFloat(formData.min_value),
        max_value: parseFloat(formData.max_value),
        cgst_rate: parseFloat(formData.cgst_rate),
        sgst_rate: parseFloat(formData.sgst_rate),
        igst_rate: parseFloat(formData.igst_rate)
      };

      if (editingRate) {
        await axios.put(`/api/gst-rates/${editingRate.id}`, payload);
        setSuccess('GST rate updated successfully');
      } else {
        await axios.post('/api/gst-rates', payload);
        setSuccess('GST rate created successfully');
      }
      resetForm();
      fetchGstRates();
    } catch (err) {
      let errorMsg = 'Failed to save GST rate';
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          errorMsg = err.response.data.detail.map(e => `${e.loc[e.loc.length - 1]}: ${e.msg}`).join(', ');
        } else {
          errorMsg = err.response.data.detail;
        }
      }
      setError(errorMsg);
    }
  };

  const handleEdit = (rate) => {
    setEditingRate(rate);
    setFormData({
      hsn_code: rate.hsn_code,
      rate_type: rate.rate_type,
      min_value: rate.min_value.toString(),
      max_value: rate.max_value.toString(),
      cgst_rate: rate.cgst_rate.toString(),
      sgst_rate: rate.sgst_rate.toString(),
      igst_rate: rate.igst_rate.toString()
    });
    setShowForm(true);
  };

  const openConfirmDialog = (title, message, confirmText, onConfirm) => {
    setConfirmDialog({
      show: true,
      title,
      message,
      confirmText,
      onConfirm,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ show: false, title: '', message: '', confirmText: 'Confirm', onConfirm: null });
  };

  const executeConfirmDialogAction = async () => {
    const action = confirmDialog.onConfirm;
    closeConfirmDialog();
    if (typeof action === 'function') {
      await action();
    }
  };

  const handleDelete = async (id) => {
    openConfirmDialog('Delete GST Rate', 'Are you sure you want to delete this GST rate?', 'Delete', async () => {
      try {
        await axios.delete(`/api/gst-rates/${id}`);
        setSuccess('GST rate deleted successfully');
        fetchGstRates();
      } catch (err) {
        setError('Failed to delete GST rate');
      }
    });
  };

  const resetForm = () => {
    setFormData({
      hsn_code: '',
      rate_type: '',
      min_value: '',
      max_value: '',
      cgst_rate: '',
      sgst_rate: '',
      igst_rate: ''
    });
    setEditingRate(null);
    setShowForm(false);
  };

  return (
    <div className="gst-manager">
      <div className="header-section">
        <h1>GST Rate Maintenance</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          + Add New Rate
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingRate ? 'Edit GST Rate' : 'Add GST Rate'}</h2>
              <button className="close-btn" onClick={resetForm}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit} className="gst-form">
                <div className="form-group">
                  <label>HSN Code *</label>
                  <input
                    type="text"
                    name="hsn_code"
                    value={formData.hsn_code}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Rate Type *</label>
                  <input
                    type="text"
                    name="rate_type"
                    value={formData.rate_type}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Min Value *</label>
                    <input
                      type="number"
                      name="min_value"
                      value={formData.min_value}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Max Value *</label>
                    <input
                      type="number"
                      name="max_value"
                      value={formData.max_value}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>CGST (%) *</label>
                    <input
                      type="number"
                      step="0.01"
                      name="cgst_rate"
                      value={formData.cgst_rate}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>SGST (%) *</label>
                    <input
                      type="number"
                      step="0.01"
                      name="sgst_rate"
                      value={formData.sgst_rate}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>IGST (%) *</label>
                    <input
                      type="number"
                      step="0.01"
                      name="igst_rate"
                      value={formData.igst_rate}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-success">
                    {editingRate ? 'Update' : 'Save'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="filter-section" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '8px', border: '1px solid #eee' }}>
        <div className="form-group" style={{ maxWidth: '300px' }}>
          <label style={{ fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>Filter by Rate Type:</label>
          <input
            type="text"
            value={filterRateType}
            onChange={(e) => setFilterRateType(e.target.value)}
            placeholder="Enter rate type..."
            style={{ width: '100%', padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc' }}
          />
        </div>
      </div>

      <div className="table-container">
        {loading ? (
          <p>Loading GST rates...</p>
        ) : (
          <table className="gst-table">
            <thead>
              <tr>
                <th>HSN Code</th>
                <th>Rate Type</th>
                <th>Min Value</th>
                <th>Max Value</th>
                <th>CGST %</th>
                <th>SGST %</th>
                <th>IGST %</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {gstRates.filter(rate => 
                rate.rate_type.toLowerCase().includes(filterRateType.toLowerCase())
              ).length > 0 ? (
                gstRates
                  .filter(rate => rate.rate_type.toLowerCase().includes(filterRateType.toLowerCase()))
                  .map((rate) => (
                    <tr key={rate.id}>
                      <td>{rate.hsn_code}</td>
                      <td>{rate.rate_type}</td>
                      <td>{rate.min_value}</td>
                      <td>{rate.max_value}</td>
                      <td>{rate.cgst_rate}%</td>
                      <td>{rate.sgst_rate}%</td>
                      <td>{rate.igst_rate}%</td>
                      <td>
                        <button className="btn btn-sm btn-info" onClick={() => handleEdit(rate)}>Edit</button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(rate.id)}>Delete</button>
                      </td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <td colSpan="8">No GST rates found matching the filter.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {confirmDialog.show && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h2>{confirmDialog.title || 'Confirm Action'}</h2>
              <button className="close-btn" onClick={closeConfirmDialog}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 0 }}>{confirmDialog.message}</p>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={closeConfirmDialog}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={executeConfirmDialogAction}>
                {confirmDialog.confirmText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GstRateManager;
